import { FeatureFlagSet } from '../models/FeatureFlags';
import packageJson from '../../package.json';
import {
  DeviceAcknowledgement,
  Release,
  RELEASE_HIGHLIGHTS_ACKNOWLEDGEMENT_KEY,
  RELEASE_HIGHLIGHTS_ACKNOWLEDGEMENT_SCHEMA_VERSION,
  RELEASE_HIGHLIGHTS_BASELINE_VERSION,
  RELEASE_HIGHLIGHTS_FEATURE_KEY,
  RELEASE_HIGHLIGHTS_WINDOW_DAYS,
  ReleaseNoteRow,
  ReleaseHighlightResult,
} from '../models/ReleaseHighlights';

const millisecondsPerDay = 24 * 60 * 60 * 1000;

declare global {
  interface Window {
    __EZRUNPBRA_RELEASE_HIGHLIGHTS_NOW__?: string;
  }
}

const getCurrentDate = () => {
  if (typeof window !== 'undefined' && window.__EZRUNPBRA_RELEASE_HIGHLIGHTS_NOW__) {
    return new Date(window.__EZRUNPBRA_RELEASE_HIGHLIGHTS_NOW__);
  }

  return new Date();
};

export interface ResolveReleaseHighlightsOptions {
  releases?: Release[];
  releaseNoteRows?: ReleaseNoteRow[];
  flagSet: FeatureFlagSet | null;
  storage?: Storage;
  now?: Date;
  currentVersion?: string;
}

const compareVersions = (left: string, right: string): number => {
  const leftParts = left.split('.').map((part) => Number(part));
  const rightParts = right.split('.').map((part) => Number(part));
  const length = Math.max(leftParts.length, rightParts.length);

  for (let index = 0; index < length; index += 1) {
    const leftPart = Number.isFinite(leftParts[index]) ? leftParts[index] : 0;
    const rightPart = Number.isFinite(rightParts[index]) ? rightParts[index] : 0;
    if (leftPart !== rightPart) return leftPart - rightPart;
  }

  return 0;
};

export const isReleaseHighlightsFeatureEnabled = (flagSet: FeatureFlagSet | null): boolean =>
  flagSet?.isEnabled(RELEASE_HIGHLIGHTS_FEATURE_KEY) ?? false;

export const loadReleaseHighlightAcknowledgement = (
  storage: Storage = window.localStorage,
): DeviceAcknowledgement | null => {
  try {
    const rawAcknowledgement = storage.getItem(RELEASE_HIGHLIGHTS_ACKNOWLEDGEMENT_KEY);
    if (!rawAcknowledgement) return null;

    const candidate = JSON.parse(rawAcknowledgement) as Partial<DeviceAcknowledgement>;
    if (
      typeof candidate.lastSeenVersion === 'string' &&
      typeof candidate.seenAt === 'string' &&
      candidate.schemaVersion === RELEASE_HIGHLIGHTS_ACKNOWLEDGEMENT_SCHEMA_VERSION
    ) {
      return candidate as DeviceAcknowledgement;
    }

    return null;
  } catch {
    return null;
  }
};

export const saveReleaseHighlightAcknowledgement = (
  releaseVersion: string,
  storage: Storage = window.localStorage,
  now: Date = new Date(),
): DeviceAcknowledgement | null => {
  const acknowledgement: DeviceAcknowledgement = {
    lastSeenVersion: releaseVersion,
    seenAt: now.toISOString(),
    schemaVersion: RELEASE_HIGHLIGHTS_ACKNOWLEDGEMENT_SCHEMA_VERSION,
  };

  try {
    storage.setItem(RELEASE_HIGHLIGHTS_ACKNOWLEDGEMENT_KEY, JSON.stringify(acknowledgement));
    return acknowledgement;
  } catch {
    return null;
  }
};

export const clearReleaseHighlightAcknowledgement = (storage: Storage = window.localStorage): boolean => {
  try {
    storage.removeItem(RELEASE_HIGHLIGHTS_ACKNOWLEDGEMENT_KEY);
    return true;
  } catch {
    return false;
  }
};

export const isReleaseMetadataValid = (release: Release): boolean =>
  Boolean(
    release.version &&
      release.title &&
      release.summary &&
      !Number.isNaN(new Date(release.releasedAt).getTime()),
  );

export const isReleaseWithinHighlightWindow = (release: Release, now: Date = new Date()): boolean => {
  const releasedAt = new Date(release.releasedAt);
  if (Number.isNaN(releasedAt.getTime())) return false;
  const age = now.getTime() - releasedAt.getTime();
  return age >= 0 && age < RELEASE_HIGHLIGHTS_WINDOW_DAYS * millisecondsPerDay;
};

export const isReleaseAtOrAfterBaseline = (release: Release): boolean =>
  compareVersions(release.version, RELEASE_HIGHLIGHTS_BASELINE_VERSION) >= 0;

export const isReleaseNewerThanAcknowledgement = (
  release: Release,
  acknowledgement: DeviceAcknowledgement | null,
): boolean => !acknowledgement || compareVersions(release.version, acknowledgement.lastSeenVersion) > 0;

export const getWhatsNewVersionKey = (version: string = packageJson.version): string => {
  const [major, minor] = version.split('.');
  return [major, minor].filter(Boolean).join('.');
};

export const getReleasesFromReleaseNoteRows = (rows: ReleaseNoteRow[] = []): Release[] => {
  const releases = new Map<string, Release>();

  rows.forEach((row) => {
    const version = row.version.trim();
    const releasedAt = row.releasedAt.trim();
    const title = row.title.trim();
    const summary = row.summary.trim();

    if (!version || !releasedAt || !title || !summary || Number.isNaN(new Date(releasedAt).getTime())) {
      return;
    }

    if (releases.has(version)) return;

    releases.set(version, {
      version,
      releasedAt,
      title,
      summary,
    });
  });

  return Array.from(releases.values());
};

export const resolveReleaseHighlights = ({
  releases,
  releaseNoteRows,
  flagSet,
  storage = window.localStorage,
  now = getCurrentDate(),
  currentVersion = packageJson.version,
}: ResolveReleaseHighlightsOptions): ReleaseHighlightResult => {
  if (!isReleaseHighlightsFeatureEnabled(flagSet)) {
    return { release: null, releases: [] };
  }

  const currentVersionKey = getWhatsNewVersionKey(currentVersion);
  const releaseCandidates = releases || getReleasesFromReleaseNoteRows(releaseNoteRows);
  const acknowledgement = loadReleaseHighlightAcknowledgement(storage);
  const validReleases = releaseCandidates
    .filter(isReleaseMetadataValid)
    .filter(isReleaseAtOrAfterBaseline)
    .filter((release) => compareVersions(release.version, currentVersionKey) <= 0);
  const currentRelease = validReleases.find((release) => release.version === currentVersionKey) || null;

  if (
    !currentRelease ||
    !isReleaseWithinHighlightWindow(currentRelease, now) ||
    !isReleaseNewerThanAcknowledgement(currentRelease, acknowledgement)
  ) {
    return { release: null, releases: [] };
  }

  const unseenReleases = validReleases
    .filter((release) => isReleaseNewerThanAcknowledgement(release, acknowledgement))
    .sort((left, right) => compareVersions(left.version, right.version));

  return { release: currentRelease, releases: unseenReleases };
};
